import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { FaEnvelope, FaExternalLinkAlt, FaLifeRing } from "react-icons/fa";
import SiteLayout from "../../components/layout/SiteLayout";
import PageIntro from "../../components/ui/PageIntro";

const SUPPORT_EMAIL = "vishwaramm@gmail.com";

const APP_SUPPORT_CONFIG = {
  "meditation-counter": {
    name: "Meditation Counter",
    platform: "iOS",
    storeUrl: "https://apps.apple.com/us/app/meditation-counter/id1559832205",
    supportUrl: "https://mantracounter.wordpress.com/",
    intro: "I handle questions, bugs, feature requests, and account issues here.",
  },
  "body-analyzer": {
    name: "Body Analyzer",
    platform: "iOS",
    storeUrl:
      "https://apps.apple.com/us/app/body-analyzer-plus/id1489053077?itsct=apps_box_badge&itscg=30200",
    intro: "I handle questions, bugs, feature requests, and support requests here.",
  },
  "neon-jumper-199x": {
    name: "Neon Jumper 199X",
    platform: "iOS",
    storeUrl: "https://apps.apple.com/gb/app/neon-jumper-199x/id6760213808",
    intro: "I handle questions, bugs, feature requests, and support requests here.",
  },
  "crystal-relics": {
    name: "Crystal Relics",
    platform: "iOS",
    storeUrl: "https://apps.apple.com/us/app/crystal-relics/id6761284126",
    intro: "I handle questions, bugs, feature requests, and support requests here.",
  },
  default: {
    name: "App Support",
    platform: "Mobile App",
    intro: "I handle questions, bugs, feature requests, and billing issues here.",
  },
};

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function getMailtoLink({
  appName,
  name,
  email,
  version,
  device,
  subject,
  message,
}) {
  const emailSubject = subject || `${appName} support request`;
  const emailBody = [
    `App: ${appName}`,
    `Name: ${name || "N/A"}`,
    `Email: ${email || "N/A"}`,
    `App Version: ${version || "N/A"}`,
    `Device / OS: ${device || "N/A"}`,
    "",
    "Message:",
    message || "",
  ].join("\n");

  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    emailSubject
  )}&body=${encodeURIComponent(emailBody)}`;
}

export default function AppSupportPage() {
  const query = useQuery();
  const appKey = query.get("app") || "default";
  const supportConfig =
    APP_SUPPORT_CONFIG[appKey] || APP_SUPPORT_CONFIG.default;
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    version: "",
    device: "",
    subject: "",
    message: "",
  });
  const [error, setError] = useState("");

  const mailtoLink = useMemo(
    () =>
      getMailtoLink({
        appName: supportConfig.name,
        ...formData,
      }),
    [formData, supportConfig.name]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setError("");
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formData.email || !formData.message) {
      setError("Email and message are required.");
      return;
    }

    window.location.href = mailtoLink;
  };

    return (
      <SiteLayout>
        <main className="page-shell home-v3-shell">
          <PageIntro
            eyebrow={`Support / ${supportConfig.platform}`}
            title={supportConfig.name}
            description={supportConfig.intro}
            actions={[
              { label: "Email", href: `mailto:${SUPPORT_EMAIL}`, external: true, variant: "primary" },
              ...(supportConfig.storeUrl
                ? [{ label: "Store", href: supportConfig.storeUrl, external: true, target: "_blank", variant: "secondary" }]
                : []),
            ]}
          />

          <section className="section">
            <div className="home-v3-band-label">
              <span className="home-v3-kicker">Support</span>
              <h2>How I want details sent.</h2>
              <p>Send the app name, device, version, and anything that helps me reproduce the issue.</p>
            </div>
            <div className="home-v3-panel">
              <div className="support-contact-list">
                <a className="contact-link" href={`mailto:${SUPPORT_EMAIL}`}>
                  <FaEnvelope /> {SUPPORT_EMAIL}
                </a>
                {supportConfig.storeUrl ? (
                  <a
                    className="contact-link"
                    href={supportConfig.storeUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FaExternalLinkAlt /> Store listing
                  </a>
                ) : null}
                {supportConfig.supportUrl ? (
                  <a
                    className="contact-link"
                    href={supportConfig.supportUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FaLifeRing /> Support page
                  </a>
                ) : null}
              </div>
              <div className="support-help-card">
                <div className="timeline-meta">Details I need</div>
                <div className="pill-list" style={{ marginTop: "12px" }}>
                  <span className="pill">App version</span>
                  <span className="pill">Phone model</span>
                  <span className="pill">iOS version</span>
                  <span className="pill">Steps to reproduce</span>
                  <span className="pill">Screenshots</span>
                </div>
              </div>
            </div>
          </section>

          <section className="section">
            <div className="home-v3-band-label">
              <span className="home-v3-kicker">Support Form</span>
              <h2>Tell me what happened.</h2>
              <p>I use the form for a complete report when email is easier than the store support page.</p>
            </div>
            <div className="home-v3-panel">
              <form className="birth-chart-form" onSubmit={handleSubmit}>
                <label className="form-field">
                  <span>Name</span>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-control modern-input"
                    placeholder="Name"
                  />
                </label>
                <label className="form-field">
                  <span>Email</span>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-control modern-input"
                    placeholder="email@example.com"
                    required
                  />
                </label>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-field">
                      <span>App version</span>
                      <input
                        type="text"
                        name="version"
                        value={formData.version}
                        onChange={handleChange}
                        className="form-control modern-input"
                        placeholder="1.0.0"
                      />
                    </label>
                  </div>
                  <div className="col-md-6">
                    <label className="form-field">
                      <span>Device / OS</span>
                      <input
                        type="text"
                        name="device"
                        value={formData.device}
                        onChange={handleChange}
                        className="form-control modern-input"
                        placeholder="iPhone 15, iOS 18"
                      />
                    </label>
                  </div>
                </div>
                <label className="form-field">
                  <span>Subject</span>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="form-control modern-input"
                    placeholder={`${supportConfig.name} support request`}
                  />
                </label>
                <label className="form-field">
                  <span>Message</span>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-control modern-input"
                    rows={5}
                    placeholder="Describe the issue, expected result, and anything else that helps."
                    required
                  />
                </label>
                {error ? <p className="birth-chart-error">{error}</p> : null}
                <div className="support-actions">
                  <button type="submit" className="home-v3-button is-primary">
                    Email me the report
                  </button>
                </div>
              </form>
            </div>
          </section>
      </main>
    </SiteLayout>
  );
}
